import React, { useEffect, useState } from "react";
import {
  Badge,
  Card,
  DisabledText,
  ErrorState,
  Grid,
  Inline,
  LoadingState,
  Section,
  Stack,
} from "@KristianIV/common-ui";
import RouteButton from "../shell/RouteButton";
import { goToAdminCommanderByUserId } from "../login/LoginByEmail";
import { getGunshotStatistics } from "../services/adminService";
import { GunshotStatisticsForTimeRanges, sortByActivity, summarizeListening } from "./gunshotStatistics";
import styles from "./Dashboard.module.css";

const REFRESH_MS = 60 * 1000;

const Metric = ({ label, value }: { label: string; value: number }) => (
  <div className={styles.metric}>
    <div className={styles.metricLabel}>{label}</div>
    <div className={`${styles.metricValue} ${value === 0 ? styles.metricZero : ""}`}>{value.toLocaleString()}</div>
  </div>
);

const OrganisationCard = ({ statistics, jwtToken }: { statistics: GunshotStatisticsForTimeRanges; jwtToken: string }) => (
  <Card interactive={false} padded>
    <Stack gap="lg">
      <Inline justify="between" wrap={false} gap="md" className={styles.orgTitle}>
        <Inline wrap={false} gap="sm" className={styles.orgIdentity}>
          <button
            type="button"
            className={styles.orgName}
            title={`${statistics.organisationName} · admin login to Commander`}
            onClick={() => goToAdminCommanderByUserId(statistics.userId, jwtToken)}
          >
            {statistics.organisationName}
          </button>
          {statistics.activeScouts > 0 && <Badge color="green">{statistics.activeScouts} listening</Badge>}
        </Inline>
        <RouteButton to={`/organisation/${statistics.organisationId}`} size="small">
          Dashboard
        </RouteButton>
      </Inline>
      <div className={styles.metrics}>
        <Metric label="Shots last hour" value={statistics.lastHourCount} />
        <Metric label="Shots last 24 h" value={statistics.last24HoursCount} />
        <Metric label="Shots last week" value={statistics.lastWeekCount} />
      </div>
    </Stack>
  </Card>
);

/** Gunshot counts per organisation, most active first, refreshed every minute like before. */
const OrganisationActivity = ({ jwtToken }: { jwtToken: string }) => {
  const [statistics, setStatistics] = useState<GunshotStatisticsForTimeRanges[] | undefined>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      getGunshotStatistics(jwtToken)
        .then((result) => {
          if (!cancelled) {
            setStatistics(result);
            setFailed(false);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setFailed(true);
          }
        });
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [jwtToken]);

  if (!statistics) {
    return failed ? (
      <ErrorState message="Could not load gunshot statistics" />
    ) : (
      <LoadingState message="Loading gunshot activity..." />
    );
  }

  const listening = summarizeListening(statistics);
  const rows = sortByActivity(statistics);

  return (
    <Section
      title="Gunshot activity"
      trailing={
        <span className={styles.meta}>
          {statistics.length} {statistics.length === 1 ? "organisation" : "organisations"} · {listening.scouts} scouts
          listening · most active first
        </span>
      }
    >
      {rows.length === 0 ? (
        <DisabledText>No gunshots the last week</DisabledText>
      ) : (
        <Grid>
          {rows.map((row) => (
            <OrganisationCard key={row.organisationId} statistics={row} jwtToken={jwtToken} />
          ))}
        </Grid>
      )}
    </Section>
  );
};

export default OrganisationActivity;
